<template>
  <div
    data-testid="public-template-1"
    class="bg-white text-t1-neutral-dark antialiased font-font-display rms-template-1-root"
    :style="wrapperStyle"
  >
    <Template1Header
      :name="restaurant.name"
      :viewer="restaurant.viewer"
      :owner-view-mode="ownerViewMode"
      :languages="languages"
      :current-locale="currentLocale"
      :reservation-available="restaurant.reservation_available === true"
      :slug="restaurant.slug || ''"
      :primary-color="restaurant.primary_color || null"
      :view-list-label="viewListLabel"
      :nav-label="navLabel"
      :view-list-aria-label="viewListAriaLabel"
      :reserve-button-label="reserveButtonLabel"
      :reserve-modal-heading="reserveModalHeading"
      :reserve-pax-label="reservePaxLabel"
      :feedback-available="restaurant.feedback_available === true"
      @select-locale="$emit('select-locale', $event)"
    />
    <main>
      <Template1Hero
        :name="restaurant.name"
        :tagline="restaurant.tagline"
        :description="(restaurant.description || '').trim()"
        :logo-url="restaurant.logo_url || ''"
        :banner-url="restaurant.banner_url"
        :show-owner-placeholder="ownerViewMode && (restaurant.viewer?.is_owner === true)"
        :operating-hours="effectiveOperatingHours"
        :view-list-label="viewListLabel"
        :has-about="hasAbout"
        :viewer="restaurant.viewer"
        :owner-view-mode="ownerViewMode"
      />
      <div
        v-motion
        class="border-t border-t1-border"
        :initial="sectionMotion.initial"
        :enter="sectionMotion.enter"
      >
        <Template1Menu
          :menu-groups="restaurant.menu_groups || []"
          :menu-items="restaurant.menu_items || []"
          :currency="restaurant.currency || 'USD'"
          :show-currency="restaurant.show_currency !== false"
          :primary-color="restaurant.primary_color"
          :section-title="menuSectionTitle"
          :empty-message="menuEmptyMessage"
          :show-owner-edit-fab="ownerViewMode"
          @select-item="onSelectMenuItem"
        />
      </div>
      <PublicMenuItemDetailModal
        v-model="detailOpen"
        v-model:selected-modifier-uuids="detailModifierUuids"
        :item="detailItem"
        :selected-sku="detailSelectedSku"
        :category-name="detailCategoryName"
        :primary-color="restaurant.primary_color"
        :currency="restaurant.currency || 'USD'"
        :show-currency="restaurant.show_currency !== false"
        :quick-order-available="!!injectedQuickOrderAvailable"
        :cart-item-quantity="detailCartQty"
        :business-type="restaurant.business_type || 'restaurants'"
        :require-variant-selection="detailRequiresVariantSelection"
        @update:model-value="onDetailClose"
        @update:selected-sku="onDetailSelectedSku"
        @add-to-cart="onAddToCart"
      />
      <section
        v-if="restaurant.feedback_available"
        id="reviews"
        v-motion
        class="rms-reviews-and-feedback border-t border-t1-border"
        aria-labelledby="reviews-section-heading"
        :initial="sectionMotion.initial"
        :enter="sectionMotion.enter"
      >
        <div class="rms-reviews-and-feedback__inner max-w-6xl mx-auto px-4 sm:px-6 py-12 md:py-16">
          <h2 id="reviews-section-heading" class="rms-reviews-and-feedback__title text-2xl font-bold text-t1-neutral-dark mb-8 md:mb-10">
            {{ $t('public.reviewsAndFeedback') }}
          </h2>
          <div class="rms-reviews-feedback-grid grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-[2rem]">
            <div class="rms-reviews-feedback-grid__reviews min-w-0">
              <Template1Reviews
                :feedbacks="restaurant.feedbacks || []"
                :primary-color="restaurant.primary_color"
                :empty-message="$t('public.noReviewsYet')"
              />
            </div>
            <div class="rms-reviews-feedback-grid__form min-w-0">
              <slot name="feedback-form" />
            </div>
          </div>
        </div>
      </section>
      <div
        v-else-if="restaurant.viewer?.is_owner && ownerViewMode"
        v-motion
        class="border-t border-t1-border"
        :initial="sectionMotion.initial"
        :enter="sectionMotion.enter"
      >
        <div class="max-w-6xl mx-auto px-4 sm:px-6 py-8">
          <div class="py-4 px-4 rounded border border-dashed border-t1-neutral-dark/40 bg-t1-neutral-dark/5">
            <p class="text-sm font-mono text-t1-neutral-dark/70">{{ $t('public.noFeedbackAvailableOwnerMessage') }}</p>
          </div>
        </div>
      </div>
      <div
        v-motion
        class="border-t border-t1-border"
        :initial="sectionMotion.initial"
        :enter="sectionMotion.enterAbout"
      >
        <Template1About
          :text="(restaurant.description || '').trim()"
          :viewer="restaurant.viewer"
          :owner-view-mode="ownerViewMode"
        />
      </div>
      <div
        v-motion
        class="border-t border-t1-border"
        :initial="sectionMotion.initial"
        :enter="sectionMotion.enterContact"
      >
        <Template1Contact
          :contacts="restaurant.contacts || []"
          :operating-hours="effectiveOperatingHours"
        />
      </div>
      <PublicMailingListSection
        v-if="restaurant.mailing_list_available"
        :restaurant="restaurant"
        template="template-1"
      />
      <div
        v-else-if="restaurant.viewer?.is_owner && ownerViewMode"
        v-motion
        class="border-t border-t1-border"
        :initial="sectionMotion.initial"
        :enter="sectionMotion.enter"
      >
        <div class="max-w-6xl mx-auto px-4 sm:px-6 py-8">
          <div class="py-4 px-4 rounded border border-dashed border-t1-neutral-dark/40 bg-t1-neutral-dark/5">
            <p class="text-sm font-mono text-t1-neutral-dark/70">{{ $t('public.noMailingListAvailableOwnerMessage') }}</p>
          </div>
        </div>
      </div>
      <Template1Map
        :restaurant="restaurant"
        :viewer="restaurant.viewer"
        :owner-view-mode="ownerViewMode"
      />
    </main>
    <div
      v-motion
      :initial="footerMotion.initial"
      :enter="footerMotion.enter"
    >
      <Template1Footer
        :restaurant-name="restaurant.name"
        :logo-url="restaurant.logo_url || ''"
        :show-credits="!restaurant.hide_footer_credits"
        :modal-locale="restaurant.locale || restaurant.default_locale || 'en'"
      />
    </div>
  </div>
</template>

<script setup>
import { ref, computed, nextTick, inject } from 'vue'
import { useReducedMotion } from '@/composables/useReducedMotion'
import { DAY_KEYS, hasOperatingHoursSchedule } from '@/utils/availability'
import { restaurantHasMapOrLink } from '@/utils/googleMapsUrl'
import Template1Header from '@/components/public/templates/template-1/Template1Header.vue'
import Template1Hero from '@/components/public/templates/template-1/Template1Hero.vue'
import Template1Menu from '@/components/public/templates/template-1/Template1Menu.vue'
import Template1Reviews from '@/components/public/templates/template-1/Template1Reviews.vue'
import Template1About from '@/components/public/templates/template-1/Template1About.vue'
import Template1Contact from '@/components/public/templates/template-1/Template1Contact.vue'
import Template1Map from '@/components/public/templates/template-1/Template1Map.vue'
import Template1Footer from '@/components/public/templates/template-1/Template1Footer.vue'
import PublicMailingListSection from '@/components/public/PublicMailingListSection.vue'
import PublicMenuItemDetailModal from '@/components/public/PublicMenuItemDetailModal.vue'
import { buildQuickOrderCartLinePayload } from '@/utils/publicMenuCart.js'
import { useToastStore } from '@/stores/toast'
import { useI18n } from 'vue-i18n'
import { useBusinessTypeLabels } from '@/composables/useBusinessTypeLabels'

const { t } = useI18n()
const toastStore = useToastStore()
const { sectionTitle: menuSectionTitle, emptyMessage: menuEmptyMessage, viewListLabel, navLabel, viewListAriaLabel, reserveButtonLabel, reserveModalHeading, reservePaxLabel } = useBusinessTypeLabels(() => props.restaurant, t)
const injectedCart = inject('quickOrderCart', ref(null))
const injectedQuickOrderAvailable = inject('quickOrderAvailable', ref(false))

const props = defineProps({
  restaurant: { type: Object, required: true },
  /** When false, owner-only UI (e.g. needs-data notice) is hidden. */
  ownerViewMode: { type: Boolean, default: true },
  languages: { type: Array, default: () => [] },
  currentLocale: { type: String, default: '' },
})

defineEmits(['select-locale'])

/** Default business logo on public sites when the restaurant has no logo set. */
const DEFAULT_HOURS = Object.freeze(
  Object.fromEntries(DAY_KEYS.map(day => [day, { open: true, slots: [{ from: '09:00', to: '21:00' }] }]))
)

const effectiveOperatingHours = computed(() => {
  const hours = props.restaurant.operating_hours
  if (hours == null) return null
  return typeof hours === 'object' && Object.keys(hours).length > 0 ? hours : DEFAULT_HOURS
})

const hasOperatingHours = computed(() => hasOperatingHoursSchedule(effectiveOperatingHours.value))
const hasAbout = computed(() => ((props.restaurant?.description || '').trim().length > 0))
const hasMapOrLink = computed(() => restaurantHasMapOrLink(props.restaurant))

const detailItem = ref(null)
const detailCategoryName = ref('Menu')
const detailSelectedSku = ref(null)
const detailOpen = ref(false)
const detailModifierUuids = ref([])
const detailRequiresVariantSelection = computed(
  () => detailItem.value?.type === 'with_variants' && detailSelectedSku.value == null,
)
let detailTriggerEl = null

function onSelectMenuItem(payload) {
  detailItem.value = payload?.item ?? null
  detailCategoryName.value = payload?.categoryName ?? 'Menu'
  detailSelectedSku.value = payload?.sku ?? null
  detailTriggerEl = payload?.trigger ?? null
  detailOpen.value = true
}

function onDetailSelectedSku(sku) {
  detailSelectedSku.value = sku
}

function onDetailClose(open) {
  if (!open) {
    detailItem.value = null
    detailCategoryName.value = 'Menu'
    detailSelectedSku.value = null
    detailModifierUuids.value = []
    nextTick(() => {
      if (detailTriggerEl && typeof detailTriggerEl.focus === 'function') {
        detailTriggerEl.focus()
      }
      detailTriggerEl = null
    })
  }
}

const detailCartQty = computed(() => {
  const c = injectedCart.value
  if (!c || !detailItem.value?.uuid) return 0
  return c.getItemQuantity(
    detailItem.value.uuid,
    detailSelectedSku.value?.uuid ?? null,
    detailModifierUuids.value,
  )
})

function onAddToCart({ item, quantity, selectedSku, modifier_uuids, replace }) {
  const c = injectedCart.value
  if (!c || !item) return
  const line = buildQuickOrderCartLinePayload({
    item,
    selectedSku,
    modifier_uuids: modifier_uuids ?? [],
  })
  const isUpdate = c.getItemQuantity(item.uuid, line.variant_uuid, line.modifier_uuids) > 0
  c.addItem({ ...line, quantity, replace: replace === true })
  toastStore.success(t(isUpdate ? 'public.quickOrder.itemUpdated' : 'public.quickOrder.itemAdded'))
}

const reducedMotion = useReducedMotion()

const sectionMotion = computed(() => {
  if (reducedMotion.value) {
    return {
      initial: { opacity: 1, y: 0 },
      enter: { opacity: 1, y: 0, transition: { duration: 0 } },
      enterAbout: { opacity: 1, y: 0, transition: { duration: 0 } },
      enterContact: { opacity: 1, y: 0, transition: { duration: 0 } },
    }
  }
  return {
    initial: { opacity: 0, y: 20 },
    enter: { opacity: 1, y: 0, transition: { duration: 0.4, ease: 'easeOut', delay: 80 } },
    enterAbout: { opacity: 1, y: 0, transition: { duration: 0.4, ease: 'easeOut', delay: 100 } },
    enterContact: { opacity: 1, y: 0, transition: { duration: 0.4, ease: 'easeOut', delay: 100 } },
  }
})

const footerMotion = computed(() => {
  if (reducedMotion.value) {
    return { initial: { opacity: 1 }, enter: { opacity: 1, transition: { duration: 0 } } }
  }
  return { initial: { opacity: 0 }, enter: { opacity: 1, transition: { duration: 0.25, ease: 'easeOut' } } }
})

const wrapperStyle = computed(() => ({
  '--rms-public-primary': props.restaurant?.primary_color || '#1152d4',
  '--t1-primary': props.restaurant?.primary_color || '#1152d4',
  '--t1-text': '#0f172a',
  '--t1-text-muted': '#64748b',
  '--t1-border': '#dbdfe6',
  '--t1-bg': '#f6f6f8',
  '--t1-bg-elevated': '#ffffff',
  '--t1-input-bg': '#ffffff',
  '--t1-radius': '8px',
  '--t1-radius-lg': '14px',
  '--t1-error': '#dc2626',
  '--t1-success': '#16a34a',
}))
</script>
